import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Account-related error definitions for /api/account/* endpoints.
 * Per project convention, account errors live in their own domain map —
 * they are NOT routed through parseSupabaseError (auth-scoped).
 */
export const ACCOUNT_ERRORS: Record<string, ErrorDefinition> = {
  // Backend codes (UPPERCASE)
  AUTHENTICATION_ERROR: {
    title: 'Session expired',
    message: 'Your session has expired so we couldn\'t complete the request.',
    action: 'Please sign in again to continue.',
    retryable: false,
    category: 'authentication',
  },

  STRIPE_CANCEL_FAILED: {
    title: "Couldn't cancel your subscription",
    message: "We had trouble canceling your subscription before deleting your account, so we've paused the deletion. We've been notified.",
    action: 'Please try again in a few moments, or contact support.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message: "We had trouble deleting your account, but we've been notified.",
    action: 'Please try again in a few moments.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  // Client fallback codes (lowercase)
  delete_failed: {
    title: "Couldn't delete your account",
    message: "We had trouble deleting your account. We've been notified.",
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  network_offline: {
    title: "You're offline",
    message: 'Deleting your account requires an internet connection.',
    action: 'Please connect to the internet and try again.',
    retryable: true,
    category: 'network',
  },
};

export function getAccountError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...ACCOUNT_ERRORS.network_offline, code: 'network_offline' };
  }

  const errorDef = ACCOUNT_ERRORS[code] ?? {
    title: 'Something went wrong',
    message: "An unexpected error occurred. We've been notified.",
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return { ...errorDef, code };
}
